import { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  EMAIL_REQUESTS,
  EMAIL_STATUSES,
  clerkEmailDraft,
  emailBody,
  emailProblem,
  invoiceNumberOf,
  sendClerkEmail,
  withCurrentGreeting,
} from "@/lib/clerkEmail";

const fieldLabel = "mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500";

// The email opened by choosing "Email Clerk" or "Email Instructor" on a fee slip row
// (src/lib/clerkEmail.js). The parent keys it on the row and the status, so changing the
// status starts a fresh draft.
// onSent({ to }) is called once the email has gone. signOff is the name under "Kind regards,".
export default function ClerkEmailComposer({ row, status, rows, signOff, onCancel, onSent }) {
  const [request, setRequest] = useState("copy");
  const [freeText, setFreeText] = useState("");
  const [draft, setDraft] = useState(() => clerkEmailDraft({ row, status, rows, signOff }));
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const mode = EMAIL_STATUSES[status];
  if (!draft || !mode) return null;

  const set = (field) => (event) => setDraft((current) => ({ ...current, [field]: event.target.value }));
  // Choosing what to ask, or typing the wording, writes the message again.
  const rewrite = (nextRequest, nextText) => {
    setRequest(nextRequest);
    setFreeText(nextText);
    setDraft((current) => ({
      ...current,
      body: emailBody({ request: nextRequest, number: invoiceNumberOf(row), freeText: nextText, signOff }),
    }));
  };
  const label = `${mode.recipient} email for ${row.clientName || "this invoice"}`;

  const send = async () => {
    const problem = emailProblem(draft);
    if (problem) {
      toast.error(problem);
      return;
    }
    setSending(true);
    try {
      const message = { ...draft, body: withCurrentGreeting(draft.body) };
      setDraft(message);
      await sendClerkEmail(message);
      setSentTo(draft.to);
      toast.success(`Email sent to ${draft.to}.`);
      onSent?.({ to: draft.to });
    } catch (error) {
      toast.error(error.message || "The email could not be sent.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-sky-200 bg-sky-50/60 p-2" aria-label={label}>
      <p className="flex items-center gap-1 text-[11px] font-semibold text-sky-900">
        <Mail className="h-3.5 w-3.5" /> Email the {mode.recipient}
      </p>
      <div>
        <Label className={fieldLabel}>To</Label>
        <Input value={draft.to} onChange={set("to")} placeholder="name@example.com" aria-label={`To, ${label}`} className="h-8 bg-white text-xs" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className={fieldLabel}>CC</Label>
          <Input value={draft.cc} onChange={set("cc")} aria-label={`CC, ${label}`} className="h-8 bg-white text-xs" />
        </div>
        <div>
          <Label className={fieldLabel}>BCC</Label>
          <Input value={draft.bcc} onChange={set("bcc")} aria-label={`BCC, ${label}`} className="h-8 bg-white text-xs" />
        </div>
      </div>
      <div>
        <Label className={fieldLabel}>Subject</Label>
        <Input value={draft.subject} onChange={set("subject")} aria-label={`Subject, ${label}`} className="h-8 bg-white text-xs" />
      </div>
      <div>
        <Label className={fieldLabel}>Request</Label>
        <Select value={request} onValueChange={(value) => rewrite(value, freeText)}>
          <SelectTrigger className="h-8 bg-white text-xs" aria-label={`Request, ${label}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EMAIL_REQUESTS.map((option) => (
              <SelectItem key={option.value} value={option.value} className="text-xs">{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {request !== "copy" && (
        <div>
          <Label className={fieldLabel}>{request === "amend" ? "Amend it to" : "Your message"}</Label>
          <Textarea
            value={freeText}
            onChange={(event) => rewrite(request, event.target.value)}
            rows={3}
            aria-label={`${request === "amend" ? "Amend it to" : "Your message"}, ${label}`}
            className="bg-white text-xs"
          />
        </div>
      )}
      <div>
        <Label className={fieldLabel}>Message</Label>
        <Textarea value={draft.body} onChange={set("body")} rows={7} aria-label={`Message, ${label}`} className="bg-white text-xs" />
      </div>
      {sentTo && (
        <p className="text-[11px] text-emerald-800" role="status">
          Sent to {sentTo}.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={send} disabled={sending} className="h-8 gap-1 bg-sky-700 px-3 text-[11px] font-medium text-white hover:bg-sky-800">
          <Mail className="h-3.5 w-3.5" /> {sending ? "Sending..." : sentTo ? "Send again" : "Send email"}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} variant="outline" className="h-8 border-slate-200 px-3 text-[11px]">
            Close
          </Button>
        )}
      </div>
    </div>
  );
}
